import { ListItem } from "~/ComponentsLibrary/List/List"
import { RowItem } from "../Table"
import { useTableRow } from "./useTableRow"
import { BoltIcon, PaperAirplaneIcon } from "@heroicons/react/20/solid"
import { RecurringKindBadgeFor, statusBadgeFor, getSendDateIcon } from "./row.utils"
import { formatMoney, formatMoneyFromCents } from "~/utils/formatMoney"
import { formatDateRange } from "~/utils/formatDateRange"
import classNames from "~/utils/classNames"
import IInvoice from "../../../../interfaces/IInvoice"
import Form from "../../../ui/Admin/Form"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import roundToMaxPrecision from "../../../../utils/roundToMaxPrecision"
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon as CurrencyDollarIconOutline,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline"
import DropdownButton from "../../../ui/Admin/DropdownButton"
type Props = {
  rowItem: RowItem
  isSelected: boolean
  setIsSelected: (invoice: IInvoice, isSelected: boolean) => void
  isVisibleElectric: boolean
  isMeterFieldsEditable: boolean
  setSelectedInvoice: (invoice: IInvoice) => void
  refreshLocalInvoices: () => void
}

export const RowMobile: React.FC<Props> = ({
  rowItem,
  isSelected,
  setIsSelected,
  isVisibleElectric,
  isMeterFieldsEditable,
  setSelectedInvoice,
  refreshLocalInvoices,
}) => {
  const {
    isMissingContactInfo,
    camp,
    thisRowItem,
    selectable,
    thisIndex,
    isChanged,
    isShowingElectricFields,
    hasSendDate,
    formattedDate,
    isDueDateRed,
    onChangeLastMeterReading,
    onChangeMeterReading,
    onChangeElectricUsed,
    dropdownOptionsFor,
    setValue,
  } = useTableRow({
    rowItem,
    isVisibleElectric,
    setSelectedInvoice,
    refreshLocalInvoices,
  })

  return (
    <ListItem>
      <div
        className="grid grid-cols-[min-content,8fr] gap-4"
        onClick={() => selectable && setIsSelected(thisRowItem.invoice, !isSelected)}
      >
        <div className="">
          <input
            type="checkbox"
            className={classNames(
              "transition-all duration-100 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-600",
              !selectable && "bg-gray-50 border-gray-100 hover:cursor-not-allowed",
              selectable && "hover:bg-gray-100 hover:cursor-pointer hover:border-gray-400",
            )}
            checked={isSelected}
            onChange={(e) => setIsSelected(rowItem.invoice, e.target.checked)}
            disabled={!selectable}
          />
        </div>
        <div>
          <div className="grid grid-cols-[4fr,4fr] gap-4">
            <div className="flex flex-col gap-1">
              <p className="font-semibold">{thisRowItem.siteName}</p>
              <p>{thisRowItem.camperName}</p>
              {!thisRowItem.isSiteOnly && (
                <div className="flex items-center gap-1">
                  {isMissingContactInfo && <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />}
                  <p className="font-normal text-gray-300">
                    {formatDateRange(
                      thisRowItem.reservation?.start_date as string,
                      thisRowItem.reservation?.end_date as string,
                      camp.timezone,
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col justify-start items-end gap-1">
              <div className="font-medium" onClick={(e) => e.stopPropagation()}>
                {!thisRowItem.isSiteOnly && !isMeterFieldsEditable ? (
                  <DropdownButton
                    isMenuFixed
                    stopPropagation
                    options={dropdownOptionsFor(thisRowItem.invoice)}
                    zIndex="z-0"
                  >
                    <EllipsisHorizontalIcon className="rounded-md   transition-colors duration-100 hover:bg-gray-100 text-gray-600 cursor-pointer" />
                  </DropdownButton>
                ) : !!thisRowItem.hasMeterLocationId ? (
                  <div className="h-full flex justify-center items-center">
                    <input
                      type="checkbox"
                      className={classNames(
                        "transition-all duration-100 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-600",
                        !selectable && "bg-gray-50 border-gray-100 hover:cursor-not-allowed",
                        selectable && "hover:bg-gray-100 hover:cursor-pointer hover:border-gray-400",
                      )}
                      checked={thisRowItem.isSyncingMeterReading}
                      onChange={(e) => setValue(`rowItems.${thisIndex}.isSyncingMeterReading`, e.target.checked)}
                      disabled={!selectable}
                    />
                    <span className="ml-2 text-sm text-gray-400">Sync</span>
                  </div>
                ) : null}
              </div>
              {!thisRowItem.isSiteOnly && (
                <p className="text-lg font-semibold">{formatMoney(thisRowItem.amountOwed, camp.currency)}</p>
              )}
              {isShowingElectricFields && (
                <div className="flex items-center gap-1">
                  <BoltIcon className="w-5 h-5 text-gray-400" />
                  <div
                    className={classNames(
                      "whitespace-nowrap text-md flex items-center",
                      (isChanged(`rowItems.${thisIndex}`) && "text-gray-200") || "text-gray-500 ",
                    )}
                  >
                    <>{formatMoneyFromCents(thisRowItem.electricSubtotal, camp?.currency)}</>
                  </div>
                  ·
                  <div className="text-gray-500">
                    <>
                      {roundToMaxPrecision(
                        Number(thisRowItem.currentMeterReading) - Number(thisRowItem.lastMeterReading),
                      )}
                      kWh
                    </>
                  </div>
                </div>
              )}
            </div>
          </div>
          {!isMeterFieldsEditable ? (
            <div className="w-full flex justify-between items-center mt-3">
              <div className="flex gap-4">
                {thisRowItem.reservationRecurringKind && (
                  <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
                    {RecurringKindBadgeFor(thisRowItem.reservationRecurringKind)}
                  </div>
                )}
                <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
                  {!thisRowItem.isSiteOnly && statusBadgeFor(thisRowItem.invoice)}
                </div>
              </div>
              <div>
                <div className="whitespace-nowrap text-sm text-gray-500 flex items-center justify-center">
                  {hasSendDate && (
                    <div className="flex items-center gap-1">
                      <PaperAirplaneIcon className="w-5 h-5 text-gray-600 rotate-[-40deg]" />
                      {formattedDate(thisRowItem.invoiceSendDate.toISOString())}{" "}
                      <span className="ml-2"></span>
                      {thisRowItem?.invoice?.send_date &&
                        thisRowItem?.invoice?.status &&
                        getSendDateIcon(thisRowItem.invoice.send_date, thisRowItem.invoice.status)}
                    </div>
                  )}

                  <div className={`lg:w-1/3 ${isDueDateRed ? "text-red-500" : ""}`}>
                    {!thisRowItem.isSiteOnly &&
                      (thisRowItem.invoiceDueDate?.isValid() ? formattedDate(thisRowItem.invoiceDueDate.format()) : "")}
                  </div>
                  {thisRowItem.reservation?.kind !== null &&
                    (thisRowItem.reservation?.long_term_reservation?.is_enabled_auto_charging ||
                      thisRowItem?.invoice?.is_attempted_auto_charge) &&
                    thisRowItem.invoice.status !== INVOICE_STATUSES.PAID && (
                      <div className="flex items-center">
                        {thisRowItem?.invoice?.status === INVOICE_STATUSES.DRAFT ? (
                          <ExclamationCircleIcon className="w-5 h-5 ml-2 text-yellow-600" />
                        ) : thisRowItem.invoice.status === INVOICE_STATUSES.OVERDUE ? (
                          <CurrencyDollarIconOutline className="w-5 h-5 ml-2 text-yellow-600" />
                        ) : (
                          <CurrencyDollarIconOutline className="w-5 h-5 ml-2" />
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full flex justify-between gap-1 items-center mt-3">
              {!thisRowItem.isSiteOnly && (
                <Form.NumberInput
                  label="Last (kWh)"
                  field={`rowItems.${thisIndex}.lastMeterReading`}
                  suffix="kWh"
                  className="w-full"
                  onChange={onChangeLastMeterReading}
                  min={0}
                  stopPropagation
                  disabled={thisRowItem.hasMeterLocationId}
                />
              )}
              <Form.NumberInput
                label="Current (kWh)"
                field={`rowItems.${thisIndex}.currentMeterReading`}
                suffix="kWh"
                className="w-full"
                onChange={onChangeMeterReading}
                min={0}
                stopPropagation
                disabled={thisRowItem.hasMeterLocationId}
              />
              <Form.NumberInput
                label="Used (kWh)"
                field={`rowItems.${thisIndex}.electricUsed`}
                suffix="kWh"
                className="w-full"
                onChange={onChangeElectricUsed}
                min={0}
                stopPropagation
                disabled={thisRowItem.hasMeterLocationId}
              />
            </div>
          )}
        </div>
      </div>
    </ListItem>
  )
}
